import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateOrganisation } from "../../organisations/api/update-organisation";

const featureFlagsSchema = z.object({
  isCV1Enabled: z.boolean().optional(),
});

type FeatureFlagsFormData = z.infer<typeof featureFlagsSchema>;

type FeatureFlagsFormProps = {
  initialData: PublicOrganisation;
};

function transformApiDataToFormData(
  data: PublicOrganisation,
): FeatureFlagsFormData {
  return {
    isCV1Enabled: data.isCV1Enabled ?? false,
  };
}

export function FeatureFlagsForm(props: FeatureFlagsFormProps) {
  const { initialData } = props;
  const form = useForm<FeatureFlagsFormData>({
    resolver: zodResolver(featureFlagsSchema),
    defaultValues: transformApiDataToFormData(initialData),
  });

  useEffect(() => {
    form.reset(transformApiDataToFormData(initialData));
  }, [initialData, form]);

  const { mutateAsync: updateOrganisation } = useUpdateOrganisation({
    onSuccess: (result) => {
      queryClient.setQueryData<PublicOrganisation | undefined>(
        ["organisation", initialData.id],
        result,
      );
      queryClient.setQueryData<PublicOrganisation[] | undefined>(
        ["organisations"],
        (oldData: PublicOrganisation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((org) => (org.id === result.id ? result : org));
        },
      );
      form.reset(transformApiDataToFormData(result));
    },
  });

  async function handleUpdateFeatureFlags(data: FeatureFlagsFormData) {
    try {
      await updateOrganisation({
        id: initialData.id,
        organisation: {
          isCV1Enabled: data.isCV1Enabled,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateFeatureFlags)}>
          <Card.Header title="Feature Flags" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="isCV1Enabled"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <div className="ml-2 mt-0">
                      <FormLabel className="text-base">Enable CV1</FormLabel>
                    </div>
                  </div>
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
